import type { SettingsItem } from "../types";
import { CORE_SETTINGS_SPECS } from "../coreSettingsSpecs";
import { Container } from "./container";

/** dispatched by Settings as `change` after set(): the components showing a setting follow it without a reload */
export type SettingsChangeEvent = CustomEvent<{ id: string, value: unknown, scope: string }>;

export class Settings extends EventTarget {

	readonly items: Map<string, SettingsItem> = new Map();
	private _values: Record<string, any> = {};

	constructor(readonly container: Container) {
		super();
	}

	async init(values?: Record<string, any>) {
		this.register(CORE_SETTINGS_SPECS);
		if (!this.container.ms('users')) return;
		// values may be supplied by the session bootstrap to avoid a separate request
		const fetched = values ?? await this.container.ms('users').get<Record<string, any>>('settings');

		// use the last cached settings only if the user settings cannot be fetched from the server;
		// a fetched result without any stored setting ({$version}) is valid, the cache may belong to
		// another company or still hold a setting the user has since removed
		if (!fetched) {
			this._values = {};
			const storedValues = window.localStorage.getItem('settings');
			if (storedValues) {
				try	{
					this._values = JSON.parse(storedValues);
				} catch (err) {
					window.localStorage.removeItem('settings');
				}
			}
		} else {
			this._values = fetched;
			window.localStorage.setItem('settings', JSON.stringify(this._values));
		}
	}

	register(items: SettingsItem[]) {
		items.forEach(item => this.items.set(item.id, item));
	}

	async set(id: string, value: any, scope: string = 'user', scopeId?: number) {
		if (!this.items.has(id)) {
			this.container.log.error(`Setting "${id}" not found`);
			return;
		}
		
		this._values[id] = value;
		this.dispatchEvent(new CustomEvent('change', { detail: { id, value, scope } }));
		this.container.ms('users').patch('settings', [{
			id: id,
			value: value,
			scope: scope,
			scopeId: scopeId
		}], {
			settings_scope: scope,
			settings_scope_id: scopeId,
			accounting: -1
		});
	}

	get(id: string) {
		return this._values[id] ?? this.items.get(id)?.defaultValue;
	}

}
